import { Component, HostListener, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UtilsService } from '../services/utils.service';
import { AgreementService } from '../services/agreement.service';
import { FormControl, AbstractControl } from '@angular/forms';

@Component({
  selector: 'app-agreement-widget',
  templateUrl: './agreement-widget.component.html',
  styleUrls: ['./agreement-widget.component.css']
})
export class AgreementWidgetComponent implements OnInit {

  @Input() agreement: Agreement | null = null;
  @Input() venue: Venue | null = null;
  @Input() displayClientName: boolean = false;
  @Input() displayVenueName: boolean = false;
  @Input() clientName: string = null;
  @Input() venueName: string = null;

  @Output() agreementDeleted = new EventEmitter<number>();

  dropdownOpen: boolean = false;
  firstAgreementChange: AgreementChange | null = null;
  latestAgreementChange: AgreementChange | null = null;
  futureAgreementChanges: AgreementChange[] = [];
  agreementChangesCount: number = 0;

  // edit
  editedAgreement: Agreement | null = null;
  previousAgreementDetails: AgreementChange | null = null;
  editedAgreementDetails: Partial<AgreementChange> = {};
  selectedEditPurpose: string = "";
  agreementChangeToUndo: AgreementChange | null = null;

  // modals
  disableModalSaveButton: boolean = false;
  modalErrorMessage: string = "";
  isSavingModal: boolean = false;
  showAgreementToggleStatusModal: boolean = false;
  agreementToggleStatusModalTitle: string = "";
  showAgreementEditModal: boolean = false;
  showAgreementEditOptionModal: boolean = false;
  showAgreementDeleteModal: boolean = false;
  showConfirmUndoAgreementChangeModal: boolean = false;
  showLastEventInput: boolean = false;
  showAgreementHistoryModal: boolean = false;

  dateControl = new FormControl('', [this.validEffectiveDate.bind(this)]);

  constructor(private utilService: UtilsService, private agreementService: AgreementService) { }

  ngOnInit(): void {
    if (this.agreement && this.agreement.agreement_changes) {
      this.initLatestAgreementChange();
    }
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.dropdownOpen = false;
  }

  translateGameType(type: string = "", targetChange: AgreementChange | null = null): string {
    if (!targetChange) targetChange = this.latestAgreementChange;
    if (targetChange?.event_interval === 'one-time') {
      return targetChange?.event_name || '';
    }
    let gametype = targetChange?.game_type;
    if(type !== "") {
      gametype = type;
    }
    switch (gametype) {
      case 'trivia':
        return 'Pub Quiz';
      case 'bingo':
        return 'Music Bingo';
      case 'slam':
        return 'Survey Slam';
      default:
        return targetChange?.event_name || '';
    }
  }

  validEffectiveDate(control: AbstractControl) {
    if (control.value) {
      this.previousAgreementDetails = this.getChangeOnOrPreviousTo(control.value);
      const selectedDate = new Date(control.value);
      if (this.editedAgreementDetails.event_date) {
        const editedEventDate = new Date(this.editedAgreementDetails.event_date);
        if (selectedDate.getTime() !== editedEventDate.getTime()) {
          return { invalidDate: true };
        }
      }
      else if (this.editedAgreementDetails.day_of_week) {
        const selectedDayOfWeek = selectedDate.getUTCDay() + 1; // 1 (Sunday) to 7 (Saturday)
        const newDoW = this.editedAgreementDetails.day_of_week;
        const originalDoW = this.previousAgreementDetails.day_of_week;
        const targetDoW = newDoW < originalDoW ? newDoW : originalDoW;
        if (selectedDayOfWeek !== targetDoW) {
          return { invalidDate: true };
        }
      }
    }
    return null;
  }

  onDayOfWeekChange() {
    this.dateControl.updateValueAndValidity();
  }

  initLatestAgreementChange() {
    const changes = this.agreement.agreement_changes;
    this.futureAgreementChanges = [];

    if (changes.length === 0) {
      this.latestAgreementChange = null;
      return;
    }

    changes.sort((a, b) => {
      return new Date(a.effective_date).getTime() - new Date(b.effective_date).getTime()
    });
    let latestChange = changes[0];
    let firstChange = changes[0];
    for (let i = 1; i < changes.length; i++) {
      const change = changes[i];
      const changeDate = new Date(change.effective_date);
      if (changeDate <= new Date()) {
        latestChange = change;
      }
      if (changeDate < new Date(firstChange.effective_date)) {
        firstChange = change;
      }
      else if (changeDate > new Date()) {
        this.futureAgreementChanges.push(change);
      }
    }
    this.latestAgreementChange = latestChange;
    this.firstAgreementChange = firstChange;
    this.agreementChangesCount = 1 + this.futureAgreementChanges.length;
  }

  getChangePreviousTo(date: string) {
    const changes = this.agreement.agreement_changes;
    let previousChange = null;
    for (const change of changes) {
      if (change.effective_date < date) {
        if (!previousChange || previousChange.effective_date < change.effective_date) {
          previousChange = change;
        }
      }
    }
    return previousChange;
  }

  getChangeOnOrPreviousTo(date: string) {
    const changes = this.agreement.agreement_changes;
    let previousChange = null;
    for (const change of changes) {
      if (change.effective_date <= date) {
        if (!previousChange || previousChange.effective_date < change.effective_date) {
          previousChange = change;
        }
      }
    }
    return previousChange;
  }

  getAgreementDifferences(newChange: Partial<AgreementChange>, as_object: boolean = false) {
    const latestChange = this.getChangePreviousTo(newChange.effective_date);
    if (!latestChange) return newChange;

    const differences: { field: string; value: any }[] = [];
    // compare
    let ignoreKeys = ['kt_event_id', 'effective_date', 'change_id'];
    for (const key of Object.keys(latestChange)) {
      if (ignoreKeys.includes(key)) continue;

      if (JSON.stringify(latestChange[key]) !== JSON.stringify(newChange[key])) {
        differences.push({ field: key, value: newChange[key] });
      }
    }
    if (as_object) {
      return differences.reduce((acc, curr) => {
        acc[curr.field] = curr.value;
        return acc;
      }, {} as Partial<AgreementChange>);
    }
    return differences;
  }


  getLatestEventTime() {
    const eventTime = this.latestAgreementChange?.event_time;
    if (!eventTime) return null;
    return this.utilService.convertTo12HourFormat(eventTime);
  }


  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }


  toggleAgreementStatus() {
    this.dropdownOpen = false;
    this.agreementToggleStatusModalTitle = this.agreement && this.agreement.active ? "Immediately Deactivate" : "Reactivate";
    // weekly or special
    if (this.latestAgreementChange.event_interval === 'one-time') {
      this.agreementToggleStatusModalTitle += " One-Time Event?";
    }else{
      this.agreementToggleStatusModalTitle += " Weekly Event?";
    }
    this.showAgreementToggleStatusModal = true;
    this.showLastEventInput = this.agreement?.active || this.agreement?.last_event ? true : false;
    this.editedAgreement = { ...this.agreement! };
  }

  startEditAgreement() {
    this.dropdownOpen = false;
    if (this.latestAgreementChange.event_interval === 'one-time') {
      this.selectedEditPurpose = 'details';
      this.showAgreementEdit();
    } else {
      this.showAgreementEditOptionModal = true;
    }
  }

  editAgreement(agreement: Agreement) {
    this.dropdownOpen = false;

    console.log('Edit agreement:', agreement.agreement_id);
  }

  deleteAgreement(agreement: Agreement) {
    this.dropdownOpen = false;
    this.showAgreementDeleteModal = true;
    this.editedAgreement = { ...agreement };
  }

  getLastEventValidationMessage() {
    if (this.editedAgreement?.active && !this.editedAgreement?.last_event) {
      return "Please set an end date for this event.";
    }
    if (!this.editedAgreement?.active && this.editedAgreement?.last_event && this.editedAgreement?.last_event < this.getTodayDate()) {
      return "To reactivate this event, please set the last event to today or a date in the future.";
    }
    if (this.editedAgreement?.active && this.editedAgreement?.last_event && this.editedAgreement?.last_event >= this.getTodayDate()) {
      return "To deactivate immediately, the last event must be in the past. To set an end date for today or later, you can use the 'Edit' option instead. The event will automatically deactivate once the last event date has passed.";
    }
    return "";
  }

  updateAgreementStatus() {
    if (this.getLastEventValidationMessage()) {
      return;
    }

    this.isSavingModal = true;
    this.modalErrorMessage = "";

    let updateData = {
      active: !this.agreement!.active
    };
    if (this.editedAgreement?.last_event) {
      updateData['last_event'] = this.editedAgreement.last_event;
    } else {
      updateData['last_event'] = null;
    }

    this.agreementService.updateAgreement(this.agreement!.agreement_id, updateData).subscribe({
      next: (res: {data: Agreement}) => {
        this.agreement = res.data;
        this.isSavingModal = false;
        this.showAgreementToggleStatusModal = false;
      },
      error: (err) => {
        console.error("Update Agreement Error:\n", err);
        this.isSavingModal = false;
        this.modalErrorMessage = err.error.detail || "An error occurred while updating agreement status.";
      }
    });
  }

  confirmDeleteAgreement() {
    if (!this.editedAgreement) return;

    this.isSavingModal = true;
    this.modalErrorMessage = "";

    this.agreementService.deleteAgreement(this.agreement.agreement_id, this.latestAgreementChange.kt_event_id).subscribe({
      next: (res) => {
        this.isSavingModal = false;
        this.showAgreementDeleteModal = false;
        this.agreementDeleted.emit(this.editedAgreement.agreement_id);
      },
      error: (err) => {
        console.error("Delete Agreement Error:\n", err);
        this.isSavingModal = false;
        this.modalErrorMessage = err.error.detail || "An error occurred while deleting the agreement.";
      }
    });
  }

  cancelDeleteAgreement() {
    this.showAgreementDeleteModal = false;
    this.editedAgreement = null;
    this.modalErrorMessage = "";
  }

  formatEventSchedule(): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[this.latestAgreementChange.day_of_week - 1] || '';

    const intervalLabels: Record<string, string> = {
      'one-time': '',
      'weekly': '',
      'bi-weekly': 'Every 2 weeks on ',
      'monthly-first': 'Every 1st ',
      'monthly-second': 'Every 2nd ',
      'monthly-third': 'Every 3rd ',
      'monthly-fourth': 'Every 4th ',
      'monthly-last': 'Every last ',
    };

    // one time event
    if (this.latestAgreementChange.event_interval === 'one-time' || this.latestAgreementChange.event_date) {
      const date = new Date(this.latestAgreementChange.event_date + "T00:00:00Z");
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
      return `${formattedDate} at ${this.getLatestEventTime()}`;
    }

    const prefix = intervalLabels[this.latestAgreementChange.event_interval] ?? '';

    return `${prefix}${dayName}s at ${this.getLatestEventTime()}`;
  }

  formatAgreementChangeFieldName(field: string): string {
    const fieldMap: { [key: string]: string } = {
      rate: 'Rate',
      day_of_week: 'Day of Week',
      event_time: 'Event Time',
      game_type: 'Game Type',
      event_name: 'Event Name',
      event_interval: 'Event Interval',
      event_date: 'Event Date',
      is_ihh: 'IHH',
      is_online: 'Online Event',
    };
    return fieldMap[field] || field;
  }

  getAgreementChangesMinusInitial(): AgreementChange[] {
    this.agreement.agreement_changes.sort((a, b) => {
      return new Date(a.effective_date).getTime() - new Date(b.effective_date).getTime();
    });
    return this.agreement.agreement_changes.filter((change, index) => index !== 0);
  }

  formatAgreementChangeFieldValue(field: string, value: any): string {
    if (field === 'event_time') {
      return this.utilService.convertTo12HourFormat(value);
    }

    if(value === true) return 'Yes';
    if(value === false) return 'No';

    if (field === 'day_of_week') {
      const days = ['', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[value] || value;
    }
    if (field === 'event_interval') {
      const intervalMap: { [key: string]: string } = {
        'one-time': 'One-Time',
        'weekly': 'Weekly',
        'bi-weekly': 'Bi-Weekly',
        'monthly-first': 'Monthly (1st)',
        'monthly-second': 'Monthly (2nd)',
        'monthly-third': 'Monthly (3rd)',
        'monthly-fourth': 'Monthly (4th)',
        'monthly-last': 'Monthly (Last)',
      };
      return intervalMap[value] || value;
    }
    if (field === 'game_type') {
      return this.translateGameType(value);
    }
    if (field === 'event_date') {
      const date = new Date(value);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    return value;
  }

  showAgreementEdit(){

    if(!this.selectedEditPurpose) return;
    this.showAgreementEditOptionModal = false;
    this.showAgreementEditModal = true;
    this.isSavingModal = false;
    this.disableModalSaveButton = false;
    this.modalErrorMessage = "";
    this.editedAgreement = { ...this.agreement! };
    this.previousAgreementDetails = null;

    if (this.latestAgreementChange) {
      this.editedAgreementDetails = { ...this.latestAgreementChange };
      this.editedAgreementDetails.effective_date = null;
    }
  }

  cancelAgreementEdit(){
    this.showAgreementEditModal = false;
    this.editedAgreement = null;
    this.disableModalSaveButton = false;
    this.isSavingModal = false;
    this.modalErrorMessage = "";
    this.selectedEditPurpose = "";
  }

  newAgreementChange(){
    if (this.dateControl.errors && this.dateControl.errors['invalidDate']) {
      this.modalErrorMessage = "Cannot update agreement without valid effective date.";
      return;
    }
    if (this.selectedEditPurpose === 'details') {
      let changed = this.getAgreementDifferences(this.editedAgreementDetails, true) as Partial<AgreementChange>;
      this.saveAgreementEdits(changed);
    }

    else if ( this.selectedEditPurpose === 'dates'){
      let payload = {
        contract_date: this.editedAgreement.contract_date,
        first_event: this.editedAgreement.first_event,
        last_event: this.editedAgreement.last_event || null,
      };
      this.agreementService.updateAgreement(this.editedAgreement!.agreement_id, payload)
        .subscribe({
          next: (res: {data: Agreement}) => {
            this.agreement = res.data;
            this.isSavingModal = false;
            this.showAgreementEditModal = false;
            this.editedAgreement = null;
            this.disableModalSaveButton = false;
            this.selectedEditPurpose = "";
          },
          error: (err) => {
            console.error("Update Agreement Error:\n", err);
            this.isSavingModal = false;
            this.disableModalSaveButton = false;
            this.modalErrorMessage = err.error.detail || "An error occurred while saving agreement changes.";
          }
        });
      return;
    }
  }

  onEventIntervalChange(event: Event) {
    const interval = (event.target as HTMLSelectElement).value;
    if (interval !== 'one-time') {
      this.editedAgreementDetails.event_name = this.translateGameType();
      this.editedAgreementDetails.event_date = null;
      if (this.editedAgreementDetails.day_of_week < 1) {
        this.editedAgreementDetails.day_of_week = 1;
      }
    }
    else {
      this.editedAgreementDetails.day_of_week = 0;
      this.editedAgreementDetails.event_name = '';
    }
  }

  saveAgreementEdits(payload: Partial<AgreementChange>) {
    if (!this.editedAgreement) return;
    this.isSavingModal = true;
    this.disableModalSaveButton = true;
    this.modalErrorMessage = "";

    payload.effective_date = this.editedAgreementDetails.event_interval === 'one-time'
      ? this.editedAgreementDetails.event_date
      : this.editedAgreementDetails.effective_date;
    payload.kt_venue_id = this.editedAgreementDetails.kt_venue_id;
    payload.kt_event_id = this.editedAgreementDetails.kt_event_id;

    const previousChange = this.getChangePreviousTo(this.editedAgreementDetails.effective_date);
    if (previousChange && previousChange.effective_date === payload.effective_date) {
      payload.kt_event_id = this.latestAgreementChange.kt_event_id;
    }
    if (payload.game_type && payload.event_interval !== 'one-time') {
      payload.event_name = this.translateGameType(payload.game_type);
    }

    this.agreementService.newAgreementChange(this.editedAgreement.agreement_id, payload).subscribe({
      next: (res: {data: Agreement}) => {
        this.agreement = res.data;
          console.log('Agreement updated:', this.agreement);
          this.initLatestAgreementChange();

        this.isSavingModal = false;
        this.showAgreementEditModal = false;
        this.editedAgreement = null;
        this.disableModalSaveButton = false;
        this.selectedEditPurpose = "";
      },
      error: (err) => {
        console.error("Update Agreement Error:\n", err);
        this.isSavingModal = false;
        this.disableModalSaveButton = false;
        this.modalErrorMessage = err.error.detail || "An error occurred while saving agreement changes.";
      }
    });

    // check if is_private is changed
    if (this.editedAgreement.is_private !== this.agreement.is_private) {
      this.agreementService.updateAgreement(this.editedAgreement.agreement_id, { is_private: this.editedAgreement.is_private }).subscribe({
        next: (res: {data: Agreement}) => {
          this.agreement = res.data;
        },
        error: (err) => {
          console.error("Update Agreement is_private Error:\n", err);
        }
      });
    }

  }

  setAgreementChangeToUndo(change: AgreementChange) {
    this.agreementChangeToUndo = change;
    this.showConfirmUndoAgreementChangeModal = true;
    this.modalErrorMessage = "";
    this.disableModalSaveButton = false;
    this.isSavingModal = false;
  }

  undoAgreementChange() {
    if (!this.agreement) return;

    this.isSavingModal = true;
    this.disableModalSaveButton = true;
    this.modalErrorMessage = "";

    const payload = {
      kt_event_id: this.agreementChangeToUndo?.kt_event_id,
      effective_date: this.agreementChangeToUndo?.effective_date
    };

    this.agreementService.removeAgreementChange(this.agreement.agreement_id, payload).subscribe({
      next: (res: {data: Agreement}) => {
        if (this.agreement && this.agreement.agreement_changes) {
          this.agreement = res.data;
          this.initLatestAgreementChange();
        }

        this.isSavingModal = false;
        this.disableModalSaveButton = false;
        this.showConfirmUndoAgreementChangeModal = false;
        this.agreementChangeToUndo = null;
      },
      error: (err) => {
        console.error("Undo Agreement Change Error:\n", err);
        this.isSavingModal = false;
        this.disableModalSaveButton = false;
        this.modalErrorMessage = err.error.detail || "An error occurred while undoing the agreement change.";
      }
    });
  }

  getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  getOneWeekAgoDate(): string {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return oneWeekAgo.toISOString().split('T')[0];
  }

  getMinEffectiveDate(): string {
    if (!this.agreement?.first_event) {
      return this.getTodayDate();
    }
    if (!this.agreement.first_event || this.agreement.first_event === '0000-00-00') {
      return '2016-08-01';
    }
    const firstEventDate = new Date(this.agreement.first_event);
    return firstEventDate.toISOString().split('T')[0];
  }

}
